import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showNotification } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import { QUERY_KEYS } from '@/constants/queryKeys';
import { caminoService } from '@/services/camino.service';
import { getApiErrorMessage } from '@/shared/utils';
import type { CaminoMemberImportRun } from '@/types';

const UNPROCESSABLE_ENTITY_STATUS = 422;
const SERVICE_UNAVAILABLE_STATUS = 503;

export const useStartCaminoMemberImport = (churchId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => caminoService.startCaminoMemberImport(churchId, file),
    onSuccess: (createdImport) => {
      queryClient.setQueryData<CaminoMemberImportRun[]>(
        [QUERY_KEYS.CAMINO_MEMBER_IMPORTS, churchId],
        (existingImports = []) => {
          const importsWithoutDuplicate = existingImports.filter(
            (importRun) => importRun.id !== createdImport.id
          );
          return [createdImport, ...importsWithoutDuplicate];
        }
      );

      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CAMINO_MEMBER_IMPORTS, churchId] });

      showNotification({
        title: 'Success',
        message: 'Import started',
        color: 'green',
      });
    },
    onError: (error) => {
      const status = isAxiosError(error) ? error.response?.status : undefined;

      if (status === SERVICE_UNAVAILABLE_STATUS) {
        void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CAMINO_MEMBER_IMPORTS, churchId] });
      }

      // 422 field/row errors are shown inline next to the file picker.
      if (status === UNPROCESSABLE_ENTITY_STATUS) {
        return;
      }

      showNotification({
        title: 'Error',
        message: getApiErrorMessage({
          error,
          fallback: 'Failed to start Camino member import',
        }),
        color: 'red',
      });
    },
  });
};